import type { GridColDef, GridRowModesModel, GridSingleSelectColDef } from "@mui/x-data-grid-premium";
import { GridRowModes } from "@mui/x-data-grid-premium";
import { formatISO, isValid, parseISO } from "date-fns";
import type { AnyObject } from "@entrilia/shared/types";
import type { ClientDictionaries } from "../../../../../../api/types/clientTypes";
import type { DataImportFieldValueChange, ImportFieldDefinition } from "../../../../../../api/types/dataImportTypes";
import type { ObjectTypeToLookupReferences } from "../../../../../../api/types/objectTypes";
import GridEditAutocompleteCell from "../../../../../common/grid/GridEditAutocompleteCell";
import GridEditMultiSelectCell from "../../../../../common/grid/GridEditMultiSelectCell";
import {
  fieldsEditSettings,
  fieldTypeToGridColTypeMap,
  getValueOptionsForSingleSelectField,
} from "../../../../../entityFields/helpers";
import DataImportChangeActionTag from "./DataImportChangeActionTag";
import type { DataImportChangeRowModel } from "./dataImportChangesGridTypes";
import DataImportChangesListEditActionsCell from "./DataImportChangesListEditActionsCell";
import DataImportChangesListViewActionsCell from "./DataImportChangesListViewActionsCell";
import DataImportCsvValueGridCell from "./DataImportCsvValueGridCell";

// A valid blank checkbox reads as no value, so row editing, which writes every column back, leaves it blank unless the user sets it.
const isBlankText = (value: unknown): value is string => typeof value === "string" && value.trim() === "";

const getValueColumnDefinition = (
  importFieldDefinition: ImportFieldDefinition,
  clientDictionaries: ClientDictionaries,
  lookupOptions: ObjectTypeToLookupReferences,
  isGridEditable: boolean,
  highlightChanges: boolean
): GridColDef<DataImportChangeRowModel> => {
  const colDef: GridColDef<DataImportChangeRowModel> = {
    field: importFieldDefinition.name,
    headerName: importFieldDefinition.name,
    type: fieldTypeToGridColTypeMap[importFieldDefinition.type],
    sortable: false,
    editable: isGridEditable,
    flex: 1,
    minWidth: importFieldDefinition.isObjectIdentifierField ? 200 : 150,
    cellClassName: ({ row }) => {
      const classNames = [];

      if (row.isExcluded) {
        classNames.push("excluded-row-cell");
      }

      const change = row.fieldValueChanges.find(({ key }) => key === importFieldDefinition.name);
      if (change?.validationError) {
        classNames.push("change-value-cell-error");
      }

      return classNames.join(" ");
    },
    valueGetter: (_, row) => {
      const change = row.fieldValueChanges.find(({ key }) => key === importFieldDefinition.name);
      const newValue = change?.newValue;
      if (typeof newValue === "string" && importFieldDefinition.type === "Date") {
        const result = parseISO(newValue);
        return isValid(result) ? result : undefined;
      }

      if (
        typeof newValue === "string" &&
        (importFieldDefinition.type === "Number" ||
          importFieldDefinition.type === "Money" ||
          importFieldDefinition.type === "Percent")
      ) {
        const result = parseFloat(newValue);
        return Number.isNaN(result) ? undefined : result;
      }

      if (typeof newValue === "string" && importFieldDefinition.type === "Checkbox") {
        return isBlankText(newValue) && !change?.validationError
          ? undefined
          : ["true", "1", "yes"].includes(newValue.toLowerCase());
      }

      if (
        typeof newValue === "string" &&
        (importFieldDefinition.type === "MultiSelect" || importFieldDefinition.type === "UserDefinedOptionsMultiSelect")
      ) {
        return newValue.split(fieldsEditSettings.multiselectItemsSeparator).filter(Boolean);
      }

      return newValue;
    },
    valueSetter: (value: unknown, row) => {
      if (importFieldDefinition.type === "Checkbox" && value === undefined) {
        return row;
      }

      let newValue = value;

      if (newValue instanceof Date) {
        newValue = formatISO(newValue, { representation: "date" });
      }

      if (typeof newValue === "number") {
        newValue = newValue.toString();
      }

      if (typeof newValue === "boolean") {
        newValue = newValue ? "TRUE" : "FALSE";
      }

      if (Array.isArray(newValue)) {
        newValue = newValue.join(fieldsEditSettings.multiselectItemsSeparator);
      }

      const fieldValueChanges: DataImportFieldValueChange[] = row.fieldValueChanges.map((change) =>
        change.key === importFieldDefinition.name ? { ...change, newValue } : change
      );

      return { ...row, fieldValueChanges };
    },
    renderCell: ({ row }) => (
      <DataImportCsvValueGridCell
        row={row}
        importFieldDefinition={importFieldDefinition}
        highlightChanges={highlightChanges}
      />
    ),
  };

  const selectValueOptions = getValueOptionsForSingleSelectField(
    importFieldDefinition.configuration,
    clientDictionaries,
    lookupOptions,
    { useLabelAsValue: true }
  );

  if (importFieldDefinition.type === "Select" || importFieldDefinition.type === "UserDefinedOptionsSelect") {
    (colDef as GridSingleSelectColDef<DataImportChangeRowModel>).valueOptions = selectValueOptions;
  } else if (
    importFieldDefinition.type === "MultiSelect" ||
    importFieldDefinition.type === "UserDefinedOptionsMultiSelect" ||
    importFieldDefinition.type === "MultiLookup"
  ) {
    colDef.renderEditCell = (params) => <GridEditMultiSelectCell {...params} valueOptions={selectValueOptions} />;
  } else if (importFieldDefinition.type === "Lookup") {
    colDef.renderEditCell = (params) => <GridEditAutocompleteCell {...params} valueOptions={selectValueOptions} />;
  }

  return colDef;
};

export const getColumnDefinitions = (
  importFieldDefinitions: ImportFieldDefinition[],
  clientDictionaries: ClientDictionaries,
  lookupOptions: ObjectTypeToLookupReferences,
  isGridEditable: boolean,
  highlightChanges: boolean,
  fileContainerId: string | undefined,
  rowModesModel: GridRowModesModel
): GridColDef<DataImportChangeRowModel>[] => [
  {
    field: "rowNumber",
    headerName: "#",
    sortable: false,
    width: 50,
    align: "right",
    headerAlign: "right",
    cellClassName: ({ row }) =>
      row.hasValidationErrors || row.applicationError ? "row-number-cell-error" : "row-number-cell",
  },
  {
    field: "action",
    headerName: "Status",
    sortable: false,
    width: 150,
    renderCell: ({ row }) => <DataImportChangeActionTag changeRequest={row} />,
  },
  ...importFieldDefinitions.map((field) =>
    getValueColumnDefinition(field, clientDictionaries, lookupOptions, isGridEditable, highlightChanges)
  ),
  {
    field: "gridActions",
    headerName: "Actions",
    sortable: false,
    resizable: false,
    width: 150,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => {
      const isInEditMode = isGridEditable && rowModesModel[row.id]?.mode === GridRowModes.Edit;

      const previewFielFieldKey = importFieldDefinitions.find((field) =>
        field.attributes?.includes("SourceFileNameInFileContainer")
      )?.name; // assume we have one such field per row

      return isInEditMode ? (
        <DataImportChangesListEditActionsCell row={row} />
      ) : (
        <DataImportChangesListViewActionsCell
          row={row}
          isGridEditable={isGridEditable}
          fileContainerId={fileContainerId}
          previewFileFieldKey={previewFielFieldKey}
        />
      );
    },
  },
];

export const createNewRow = (
  id: string,
  dataImportId: string,
  fileDataCatalogueId: string,
  importFieldDefinitions: ImportFieldDefinition[]
): DataImportChangeRowModel => ({
  isNew: true,
  id,
  dataImportId,
  fileDataCatalogueId,
  hasValidationErrors: false,
  isExcluded: false,
  fieldValueChanges: importFieldDefinitions.map(({ name }) => ({ key: name, fieldType: "Text" })),
});

export const getNewValuesObject = (row: DataImportChangeRowModel): AnyObject =>
  Object.fromEntries(row.fieldValueChanges.map((c) => [c.key, c.newValue ?? ""]));
